import { defineBedrockEndpointContract } from "schemas/bedrockApi/contract";
import { z } from "zod";

/**
 * External-metrics providers the analytics proxy can translate. Adding a
 * provider here forces (via exhaustiveSwitch) a translator implementation in
 * apps/bedrock-api/src/services/insights-proxy, plus shared ClickHouse views.
 */
export const INSIGHTS_PROXY_PROVIDERS = [
  "triple-whale",
  "contentsquare",
] as const;
export type InsightsProxyProvider = (typeof INSIGHTS_PROXY_PROVIDERS)[number];

/**
 * `daily` is a per-day time series (charting), `totals` is range totals with
 * previous-period comparison (KPI tiles — ratio metrics like ROAS can't be
 * derived by summing daily rows), and `metrics` is the provider's metric-id
 * roster for discovery — read from the provider's own metadata, so it is
 * window-independent (a metric with no rows in the range still appears) rather
 * than the under-reporting `SELECT DISTINCT metric` over `daily`. Each report
 * kind backs its own ClickHouse view because a `url()` call declares exactly one
 * column structure.
 */
export const INSIGHTS_PROXY_REPORTS = ["daily", "totals", "metrics"] as const;
export type InsightsProxyReport = (typeof INSIGHTS_PROXY_REPORTS)[number];

/**
 * Canonical names of each provider's shared ClickHouse proxy views, copied
 * verbatim from the analytics-database migrations that create them (no naming
 * convention is assumed). Keyed by provider so adding one to
 * `INSIGHTS_PROXY_PROVIDERS` fails compilation until its real view names are
 * listed here. The insights dashboard matches these literals against card SQL
 * to group cold-load queries per view. (Berkley, 2026-07-30, USE-2594)
 */
export const INSIGHTS_PROXY_VIEW_NAMES_BY_PROVIDER: Record<
  InsightsProxyProvider,
  readonly string[]
> = {
  "triple-whale": [
    "triplewhale_metrics_daily",
    "triplewhale_metrics_totals",
    "triplewhale_metrics_list",
  ],
  contentsquare: [
    "contentsquare_metrics_daily",
    "contentsquare_metrics_totals",
    "contentsquare_metrics_list",
  ],
};

export const INSIGHTS_PROXY_VIEW_NAMES = Object.values(
  INSIGHTS_PROXY_VIEW_NAMES_BY_PROVIDER,
).flat();

/**
 * Word-boundary test for a view/table name appearing in SQL — the one shared
 * matching primitive behind dataset inference and cold-load query grouping,
 * so the two can never disagree about what "references a view" means.
 */
export function sqlReferencesViewName(sql: string, viewName: string): boolean {
  return new RegExp(`\\b${viewName}\\b`).test(sql);
}

/**
 * Header the shared ClickHouse views forward the caller's credential in — a
 * Replo JWT (miniapp / sandbox) or a `replo_sk_` public API key. It is the
 * views' `auth_token` parameter, injected server-side from whatever
 * authenticated the analytics request (see the analytics service) — neither the
 * agent nor a saved dashboard ever writes it, and it never appears in view DDL
 * or in URL query strings.
 */
export const INSIGHTS_PROXY_AUTH_HEADER = "X-Replo-Auth-Token";

/**
 * Header the shared ClickHouse views forward the target project id in, injected
 * server-side alongside the credential. The proxy authorizes the credential
 * against this project rather than deriving identity from the token — a public
 * API key is workspace-scoped, so the project must be explicit, and for JWTs
 * the proxy still checks this matches the token's own project.
 */
export const INSIGHTS_PROXY_PROJECT_ID_HEADER = "X-Replo-Project-Id";

// (Berkley, 2026-07-13, REPL-29026): reject non-calendar dates (2026-13-45,
// 2026-02-30) that the format regex accepts — JS Date normalizes overflow, so
// a mismatch after round-tripping means the input wasn't a real date.
function isCalendarDate(value: string): boolean {
  const parsed = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(parsed.getTime()) &&
    parsed.toISOString().slice(0, 10) === value
  );
}

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
  .refine(isCalendarDate, "Not a valid calendar date");

const reportEndpoint = defineBedrockEndpointContract({
  operationId: "insightsProxy.report",
  method: "get",
  path: "/api/v1/internal/analytics-proxy/{provider}",
  routePath: "/{provider}",
  description:
    "NDJSON external-integration metrics for the shared per-provider ClickHouse url() views. Machine caller (ClickHouse); authenticated by the credential in X-Replo-Auth-Token (Replo JWT or replo_sk_ key) and authorized against the project in X-Replo-Project-Id.",
  request: {
    params: z.object({
      provider: z.enum(INSIGHTS_PROXY_PROVIDERS),
    }),
    query: z
      .object({
        report: z.enum(INSIGHTS_PROXY_REPORTS),
        since: isoDateSchema,
        until: isoDateSchema,
      })
      // Lexicographic compare is chronological for zero-padded ISO dates.
      .refine(({ since, until }) => since <= until, {
        error: "`since` must be on or before `until`",
      }),
  },
  response: { type: "binary" },
});

export const insightsProxyEndpointContracts = {
  report: reportEndpoint,
} as const;
